<template>
    
    <div>

		<OpeningPanel v-if="fetchedProject&&!!project.active&&((project.isNew&&awaitingNewResults)||!hadActivity||videosProcessed.length)">
			<template v-slot:title>
				<h2 v-if="project.isNew&&awaitingNewResults">Your project has been created :)</h2>
				<h2 v-else-if="!hadActivity">Please add some keywords</h2>
			</template>
			<template v-slot:content>
				<p v-if="!hadActivity">
					<a href="#" @click="addKeywords">Click here</a> to add some keywords and get things going.
				</p>
				<p v-if="awaitingNewResults">
					The keyword rankings are being checked at the moment, your data will be available shortly.
				</p>
				<div class="videos" v-if="videosProcessed.length>0">
					<p v-if="awaitingNewResults">Whilst you're waiting for your rankings, here's some videos that may help...</p>
					<p v-else>Here's a video that may help...</p>
					<div 
						class="video-item"
						v-for="(video, videoIndex) in videosProcessed" 
						:key="'video-current-'+videoIndex" 
						@click="loadVideo ( video.videoId )"
					>

						<div class="video-thumb"><img :src="videoImage (video.videoId)" :alt="video.title" /></div>

						<h3 class="video-title" v-html="video.title" />

					</div>
				</div>
			</template>
		</OpeningPanel>
		
		<ProjectTitle 
			:project="project"
			:readOnly="readOnly"
			v-if="fetchedProject"
		>

			<template v-slot:right>
				<div class="right-buttons" v-if="!report">

					<span 
						:class="{'large-button':true,mini:true}"
						v-html="'CSV Imports'" 
						@click="showCSVImports"
						v-if="project.isAdmin&&(project.hasCSVUploads||project.canUploadCSV)"
					/>

					<span :class="{'large-button':true,report:true,mini:true}" @click="showReport=true" v-if="hadActivity">Create Report</span>

					<span :class="{'large-button':true,mini:true}" @click="openInsights" v-if="hadActivity"><Icon type="insights" class="svg insights white" /> &nbsp;<span>Insights</span></span>

					<span 
						class="large-button mini"
						v-html="'Add keywords'"
						@click="addKeywords" 
						v-if="!hadActivity&&!readOnly"
					/>

				</div>
			</template>

		</ProjectTitle>

		<NumberTotalSection 
			v-if="hadActivity&&showIt('overview')"
			:numberTotals="projectOverview.standard"
			:donuts="true"
			:animation="!report"
			:numberTotalsSmall="projectOverview.small"
			:tabbed="true"
			:minimal="report"
			:key="projectOverviewKey"
			:hideTabButton="!showIt('buttons')"
			:showTheSmalls="showSmalls"
			@clicked="numberTotalSectionClick"
			@show-smalls="showingProjectOverviewSmalls"
		>
			<template v-slot:standard-header>
				<div class="frequency-setters" v-if="showIt('buttons')">
					<span 
						v-for="(frequency, frequencyIndex) in ['Daily','Weekly','Monthly']" 
        				:key="frequencyIndex" 
        				:class="{selected:frequencyDonuts===frequency}"
        				@click="setDonutFrequency(frequency)"
        				v-html="frequency"
					/>
				</div>
			</template>
		</NumberTotalSection>

		<div class="html2pdf__page-break" v-if="showSmalls" />

		<FilterWrap
			@change="filterChange"
			:showUpdateButton="showFilterUpdateButton"
			:currentFrequency="frequency"
			:showFilterWeekly="project.filterByWeek"
			:showFilterMonthly="project.filterByMonth"
			@zoomChange="zoomChange"
			v-if="hadActivity&&showAverageChart&&showIt('filters')"
		>

			<v-select 
	            :placeholder="'Tags'"
	            v-model="tagsSelected" 
	            :options="tags"  
	            :multiple="true"
	            @input="showFilterUpdateButton=true"
	            v-if="tags.length>0"
	        ></v-select>

		</FilterWrap>

		<PageMessage v-if="notFound" title="Project not found">
			This project could not be found in this account. If it is was shared with you do you have the correct acount selected?
		</PageMessage>

		<section class="outer-pad info-panel-multi-wrap" v-if="hadActivity&&showIt('graph')">

			<GraphWrap 
				:chartdata="theChart.data" 
				:options="theChart.options" 
				:title="averageChartLabel" 
				:graphKey="graphKey"
				:overlay="chartOverlay"
				:height="mapSmallHeight"
				size="full"
				ratio="seventy-thirty"
				@chart:render="chartRendered"
				:key="chartKey"
				@chart-click="handleChartClick"
			>
				<template slot="above-graph" v-if="hasChartData">
					<span class="svg minimize" title="Maximize" @click="minimizeGraph" v-if="smallGraph"><font-awesome-icon icon="up-right-and-down-left-from-center" /></span>
					<span class="svg minimize" title="Minimize" @click="minimizeGraph" v-if="!smallGraph"><font-awesome-icon icon="down-left-and-up-right-to-center" /></span>
				</template>
				<template v-slot:below-graph v-if="!smallGraph">

					<Competitors
						:competitors="project.competitors"
						:showCompetitors="project.showCompetitors"
						:showAdd="!readOnly"
						@save="saveCompetitors"
						:key="competitorsKey"
						v-if="showIt('buttons')&!chartOverlay"
					/>

					<EventActions 
						:project="project"
						:events="events"
						@reload-chart="updateGraph"
						class="event-actions"
					/>

				</template>
				
			</GraphWrap>

		</section>

		<section class="outer-pad" v-if="hadActivity&&!report&&!readOnly">

			<div class="action-buttons-float">

				<span 
					class="large-button"
					v-html="'Add keywords'"
					@click="addKeywords"
				/>

			</div>

		</section>

		<div class="html2pdf__page-break" v-if="breakBeforeGraph" />

		<section class="outer-pad" id="keywords-table" v-if="fetchedProject&&showIt('table')">

			<Table 
				:class="{'keyword-table': true}"
				:headings="tableHeadings" 
				:items="keywords" 
				:pages="table.pages"
				:page="table.page"
				:count="table.count"
				:loading="table.loading"
				:sorted="table.sorted"
				:key="tableKey"
				:title="showIt('buttons')?keywordCountText:''"
				:searchSetup="searchSetup"
				:forceSearchFieldUpdate="forceSearchFieldUpdate"
				:hideSearch="!showIt('filters')"
				:hidePagination="!showIt('buttons')"
				:hideHeadingShowHide="!showIt('buttons')"
				:hideIcons="report"
				:name="tableName"
				:enableHeadingFilters="showIt('filters')"
				:showCSV="!report"
				:canSave="!report"
				:showPDF="!report"
				savedSearchType="rank-tracking"
				@pagination="pagination"
				@triggerShowFeaturesTip="triggerShowFeaturesTip"
				@sort="sort"
				@search="search"
				@clear="clearSearch"
				@selected="keywordActions"
				@checkboxFilter="checkboxFilterChange"
				@selectKeyword="selectKeyword"
				@viewConflict="viewConflict"
				@addTag="addTag"
				@deletePause="deletePause"
				@updateFrequency="updateFrequency"
				@conflictClick="conflictClick"
				@viewSearchResults="viewSearchResults"
				@searchChange="searchChange"
				@forceSearchFieldUpdated="forceSearchFieldUpdate={}"
				@ready="tableReady"
				@csv="downloadKeywords('csv')"
				@pdf="downloadKeywords('pdf')"
				loadingText="Loading keywords..."
				emptyLabel="There are no keywords at the moment"
				emptySearch="There were no keywords matching your search"
				:addNewButtonLabel="(!report&&!readOnly)?'Add keywords':null"
				@addNew="addKeywords"
			/>

		</section>

		<ActionsBar v-if="keywordsSelectedProcessed._ids.length>0 && !showKeywordGraph && !generatingReport">

			<span 
				class="action-button"
				v-html="'Create graph'"
				@click="showKeywordGraph=true"
			/>
			<span 
				class="action-button"
				v-html="'Pause'"
				@click="pauseDeleteMulti ('pause')"
				v-if="!readOnly"
			/>
			<span 
				class="action-button"
				v-html="'Un-Pause'"
				@click="pauseDeleteMulti ('un-pause')"
				v-if="!readOnly"
			/>
			<span 
				class="action-button"
				v-html="'Delete'"
				@click="pauseDeleteMulti ('delete')"
				v-if="!readOnly"
			/>
			<span 
				class="action-button"
				v-html="'Update Frequency'"
				@click="updateFrequencyMulti ()"
				v-if="!readOnly"
			/>
			<span 
				class="action-button"
				v-html="'Add Alert'"
				@click="addAlert"
			/>
			<span 
				class="action-button"
				v-html="'Add Tags'"
				@click="addTags"
				v-if="!readOnly"
			/>
			<span 
				class="action-button"
				v-html="'Keyword Cannibalisation Fixed'"
				@click="conflictsFixed"
				v-if="!readOnly"
			/>
			<span 
				class="action-button"
				v-html="'Add to another project(s)'"
				@click="addKeywordsToAnotherProject"
				v-if="!readOnly"
			/>

		</ActionsBar>

		<KeywordGraph 
			v-if="showKeywordGraph" 
			@close="closeKeywordGraph"
			:preSelectedKeywords="keywordsSelectedProcessed._ids"
			:conflict="isConflict"
			:endDate="keywordGraphEndDate"
			:showList="!isConflict"
			:title="theKeywordGraphTitle"
			:competitors="project.competitors"
			:showCompetitors="project.showCompetitors"
			:projectUrl="project.url"
		/>

		<Tagger 
			v-if="showTagger" 
			:settings="taggerSettings"
			@close-tagger="closeTagger"
			:removeCharacters="[',']"
		>
			<template v-slot:editor v-if="taggerSettings.availableTags.length>0">
				
				<div class="tag-editor">

					<span @click="tagEditor">Click here to delete/edit tags</span>

				</div>

			</template>
		</Tagger>

		<TagEditor 
			v-if="showTagEditor"
			:project="project"
			@close="closeTagEditor"
		/>

		<Keywords 
			v-if="showKeywordModal" 
			@close="closeKeywordModal"
			:currentFrequency="keywordModalInfo.currentFrequency"
			:title="keywordModalInfo.title"
			:id="keywordModalInfo._id"
			:project="keywordAdderKeywords.length===0?projectId:null"
			:instantUpload="instantUpload"
			:csvUpload="keywordAdderKeywords.length===0?project.canUploadCSV:false"
			:keywords="keywordAdderKeywords"
		/>

		<SERPresults
			:results="serpResults"
			@close="closeSERPresults"
			v-if="serpResults.results.length>0"
		/>

		<csvImports
			v-if="showCSVImportsOverlay"
			:project="project"
			@close="closeCSVImports"
			@upload-csv="csvImportsUpload"
			@refresh-project="refreshAll"
		/>

		<Alerts
			v-if="showAlertsOverlay"
			:project="projectId"
			:keywords="keywordsSelectedProcessed"
			@close="closeAlertsOverlay"
		/>

		<ReportOptions
			:project="projectId" 
			:pages="table.pages" 
			:page="table.page" 
			:projectName="project.name"
			:type="(showReport==='csv'||showReport==='pdf')?showReport:''"
			v-if="showReport"
			@download="closeReportOptions"
			@close="closeReportOptions"
		/>

		<VideoPlayer 
			v-if="showVideo"
			:videoId="videoId"
			:title="videoTitle"
			@close="closeVideo"
		/>

		<FeaturesTip 
			v-if="showFeaturesTip"
			@close="showFeaturesTip=false"
		/>

		<RankingsCustomDate
			v-if="showRankingsCustomDate"
			@close="closeRankingsCustomDate"
			@added="addedCustomDate"
			:projectId="projectId"
		/>

		<InsightsModal 
			@close="closeInsights"
			:projectId="projectId"
			v-if="showInsights"
			source="rank-tracking"
			:title="insightsTitle"
		/>

    </div>

</template>

<script>
import Vue from 'vue';
import { mapState, mapMutations } from 'vuex'
import ApiService from './../services/api.service'
import { TokenService } from './../services/storage.service'
import general from './../utils/general'
import Table from './../components/Table.vue'
import GraphWrap from './../components/Data/GraphWrap.vue'
import NumberTotalSection from './../components/Data/NumberTotalSection.vue'
import FilterWrap from './../components/FilterWrap.vue'
import KeywordGraph from './../components/KeywordGraph.vue'
import RankUpDown from './../components/Display/RankUpDown.vue'
import LinkWithIcon from './../components/Display/LinkWithIcon.vue'
import KeywordURL from './../components/Display/KeywordURL.vue'
import Tagger from './../components/Tagger.vue'
import TagEditor from './../components/TagEditor.vue'
import Keywords from './../components/Keywords.vue'
import TableIcon from './../components/Display/TableIcon.vue'
import Device from './../components/Display/Device.vue'
import SERPresults from './../components/SERPresults.vue'
import Uploader from './../components/Uploader.vue'
import csvImports from './../components/csvImports.vue'
import Alerts from './../components/Alerts.vue'
import Icon from "./../components/Display/Icon.vue";
import ActionsBar from './../components/Display/ActionsBar.vue'
import Features from './../components/Display/Features.vue'
import ProjectTitle from './../components/Display/ProjectTitle.vue'
import PageMessage from './../components/PageMessage.vue'
import OpeningPanel from './../components/Display/OpeningPanel.vue'
import RankingsCustomDate from './../components/Display/RankingsCustomDate.vue'
import ReportOptions from './../components/ReportOptions.vue'
import Competitors from './../components/Competitors.vue'
import EventActions from './../components/EventActions.vue'
import VideoPlayer from './../components/VideoPlayer.vue';
import FeaturesTip from './../components/Display/FeaturesTip.vue';
import InsightsModal from './../components/Display/InsightsModal.vue';
export default { 
  	name: 'Project',
  	props: {},
  	components: { ProjectTitle, Tagger, Table, Competitors, NumberTotalSection, GraphWrap, KeywordGraph, FilterWrap, Keywords, Device, SERPresults, Uploader, csvImports, ActionsBar, Alerts, PageMessage, OpeningPanel, ReportOptions, VideoPlayer, TagEditor, FeaturesTip, EventActions, RankingsCustomDate, Icon, InsightsModal },
  	props: {
		  report: {
			  type: Boolean,
			  default:false
		  },
		  generatingReport: {
			  type: Boolean,
			  default:false
		  },
		  showForReport: {
			  type: Object
		  }
	},
	data() {

		return {
			showRankingsCustomDate: false,
			showFeaturesTip: false,
			showTagEditor: false,
			init: false,
			lala:true,
			graphKey: 0,
			competitorsKey: 0,
			showReport: false,
			awaitingReport: false,
			showSmalls:false,
			chartLoading: false,
			showAlertsOverlay: false,
			showCSVImportsOverlay: false, 
			serpResults: { results: [], keyword: '' },
			fetched: false,
			showKeywordGraph:false,
			fetchKeywordsTimeoutSet: false,
			project:{},
			filters: {},
			showUploader: false,
			keywordsSelected: [],
			showFilterUpdateButton: false,
			frequency: 'days',
			searchCleared: false,
			frequencyDonuts: 'Daily',
			reloadingChart: false,
			conflictsOnlyLast: false,
			isConflict: false,
			showKeywordModal: false, 
			ranSearchWithFreqency: false,
			keywordModalInfo: { title: null, currentFrequency: null },
			instantUpload: false,
			keywordGraphFilters: {},
			keywordGraphEndDate: new Date(),
			notFound: false,
			hideCaddyScoreLine: false,
			hideAvgScoreLine: false,
			showVideo: false,
			videoId: null,
			chartKey: 1,
			showInsights: false,
			projectOverviewKey: new Date().getTime(),
			table: {
				page: 1,
				sorted: { keyword: 1 },
				search: {},
				loading: true,
				items: []
			},
			chartData: [],
			hasKeywords: false,
			fetchedKeywords: false,
			showConflictsOnly: false, 
			zoom: {
				min: 1,
				max: 100
			},
			keywordGraphTitle: null,
			sortBy: 'keyword',
			sortDirection: 'asc',
			tags:[],
			tagsSelected: [],
			taggerSettings: {},
			showTagger: false,
			fetchedProject: false,
			tracked: false,
			stillUpdating: false,
			searches: {},
			forceSearchFieldUpdate: {},
			competitorDataSet: {},
			pdfTitle: '',
			tableKey: 0,
			keywordAdderKeywords: [],
			smallGraph: false,
			readOnly: true
		}

	},
	watch: {
		searches: {
			handler(newVal, oldVal) {
				if ( !!this.searches.tags && this.searches.tags.includes("empty-tags") && this.searches.tags.length > 1 ) {
					Vue.set(this.searches, "tags", ["empty-tags"]);
					let notTags = this.tagsProcessed.find( tag => tag.value === "empty-tags" );
					general.$snotify(this,'error','Tags',"You can't filter by tags when you have '"+notTags.label+"' selected");
				}
			},
			deep: true
		}
	},
	computed: {

		...mapState('general',['tableSortingInfo','tableHeadingVisibility','dateTimeFormat','videos']),

		fullAccess () {

			return !!this.project && this.project.access === 'full-access';

		},

		searchingGraph () {

			return !!this.filters.endDate || !!this.filters.startDate || !!this.filters.tags;

		},

		showHeadings: {

			get: function() {
				
				return general.stripObject( this.tableHeadingVisibility[this.tableName] || [] );
		
			},
			set: function(showHeadings) {
				
				this.tableHeading ( { tableName: this.tableName, sortingInfo: showHeadings } );

			}

		},

		videoTitle () {

			if ( this.videoId ) {

				let v = this.videos.find( vid => vid.videoId === this.videoId );

				if ( v ) return v.title;

			}

			return '';

		},

		videosProcessed () {

			let videos = [];

			if ( this.project.allProjectCount < 3 && ( !this.hadActivity || !this.awaitingNewResults ) ) {

				let type = this.hadActivity ? 'project-new-with-keywords' : 'project-new-no-keywords';

				this.videos.forEach( video => {

					if ( video.pageNames.indexOf(type) >= 0 ) videos.push(video);

				});

			}

			return videos;

		},

		hadActivity () {

			return this.fetchedProject && this.project.keywordsTracked;

		},

		awaitingNewResults () {

			return this.hadActivity && !!this.project.keywordsToCheck > 0;


		},

		events () {

			let events = [];

			if ( !!this.chartData && !!this.chartData.items && this.chartData.items.length > 0 && !! this.chartData.items[0].annotations.events ) {

				this.chartData.items[0].annotations.events.forEach( event => {

					let ev = {
						date: general.dateFormat ( 'F jS', event.date ),
						events: event.events
					};

					events.push( ev );

				});

			}

			return events;

		},

		theChart () {

			let _this = this,
				theChart = {
				data: {
					labels: [],
					datasets: []
				},
				options: {
					responsive: true,
					plugins: {
						legend: {
							onClick: function (event, elem) {
								if ( elem.text === 'Average Rank' ) {
									_this.hideAvgScoreLine = !_this.hideAvgScoreLine;
								}
								if ( elem.text === 'Caddy Score' ) {
									_this.hideCaddyScoreLine = !_this.hideCaddyScoreLine;
								}
								else {
									_this.project.competitors[_this.competitorDataSet[elem.datasetIndex]] = elem.hidden;
									_this.saveCompetitors (true)
								}
							},
							labels: {
								filter: function(item, chart) {
									// Hide legend items for datasets with empty labels (annotations)
									return item.text && item.text.trim() !== '';
								}
							}
						},
						tooltip: {
							callbacks: {
								label: function(context) {
									let dataset = context.dataset, 
										index = context.dataIndex;
									if ( !!dataset.isAnnotation ) {

										let max = 4;

										if ( dataset.labels[index].length > max ) {

											let labels = dataset.labels[index].slice(0,max);

											labels.push( '+' + ( dataset.labels[index].length - max ) + ' more, see events'  );

											return labels;

										}

										return dataset.labels[index];

									}
									if ( typeof dataset.tooltips !== 'undefined' && typeof dataset.tooltips[index] !== 'undefined' ) 
										return ' ' + dataset.label + ': ' + context.parsed.y + ' (' + dataset.tooltips[index] + ')';
									return ' ' + dataset.label + ': ' + context.parsed.y;
								},
								labelColor: function(context) {
									// Access the custom attribute 'showTooltipSquare' for the dataset
									const dataset = context.dataset;
									
									if ( !!dataset.isAnnotation ) {
										
										return {
											borderColor: dataset.pointBackgroundColor,
											backgroundColor: dataset.pointBackgroundColor
										};

									}
									
									return {
										borderColor: dataset.borderColor,
										backgroundColor: dataset.backgroundColor
									};
								}
							}
						}
					},
					maintainAspectRatio: false,
					scales: {
						x: {
							//min: this.zoom.min,
							//max: this.zoom.max,
							grid: {
								display: true,
								drawBorder: true,
								drawOnChartArea: false
							},
							ticks: {
								beginAtZero: true,
								autoSkip: true,
								maxTicksLimit: this.smallGraph ? 10 : 20
							}
						},
						y: {
							type: 'linear',
							display: true,
							position: 'left',
							reverse: true,
							min: this.zoom.min,
							max: this.zoom.max,
							grid: {
								display: true,
								drawBorder: true,
								drawOnChartArea: false
							},
							title: {
								display: true,
								text: 'Position'
							},
							ticks: {
								maxTicksLimit: this.smallGraph ? 5 : null
							}
						},
						y1: {
							type: 'linear',
							display: true,
							position: 'right',
							min: 100 - this.zoom.max,
							max: 100,
							grid: {
								display: true,
								drawBorder: true,
								drawOnChartArea: false
							},
							title: {
								display: true,
								text: 'Caddy Score'
							},
							ticks: {
								maxTicksLimit: this.smallGraph ? 5 : null
							}
						},
						y2: {
							type: 'linear',
							display: false,
							position: 'right',
							min: 1,
							max: 100,
							ticks: {
							},
							grid: {
								drawOnChartArea: false
							}
						}
					}
				}

			}

			if ( this.chartData ) {
				
				theChart.data.labels = this.chartData.labels || [];

				let ii = 0, annotationsDone = false;

				if ( this.chartData.items ) {

					this.chartData.items.forEach ( function (item) {

						if ( item.annotations && !annotationsDone ) {

							annotationsDone = true;

							let annotations = {
								project: [],
								global: []
							};

							item.annotations.data.forEach( function (d) {

								let project = d === 2 ? d : null,
									global = d !== 2 ? d : null;

									annotations.project.push( project );
									annotations.global.push( global );

							});

							theChart.data.datasets.push({
								label: '',
								fill: false,
								tooltips: item.tooltips,
								data: annotations.project,
								labels: item.annotations.labels,
								yAxisID: 'y2',
								spanGaps: false,
								pointBackgroundColor: '#f1492a',
								pointBorderColor: '#f1492a',
								pointStyle: 'triangle', 
								borderColor: 'rgba(0, 0, 0, 0)', 
								isAnnotation: true,
								clip: false,
								showLine: false,
								pointRadius: 6
							});

							++ii;

							theChart.data.datasets.push({
								label: '',
								fill: false,
								tooltips: item.tooltips,
								data: annotations.global,
								labels: item.annotations.labels,
								yAxisID: 'y2',
								spanGaps: false,
								pointBackgroundColor: '#394559',
								pointBorderColor: '#394559',
								pointStyle: 'triangle', 
								borderColor: 'rgba(0, 0, 0, 0)', 
								isAnnotation: true,
								clip: false,
								showLine: false,
								pointRadius: 6
							});

							++ii;

						}

						theChart.data.datasets.push({
							label: this.chartData.items.length === 1 ? "Average Rank" : item.label,
							tooltips: item.tooltips,
							fill: false,
							hidden: this.hideAvgScoreLine,
							backgroundColor: item.color,
							borderColor: item.color,
							data: item.data,
							yAxisID: 'y',
							spanGaps: false,
							tension: 0.4
						});

						++ii;

						if ( item.caddyScoreData ) {

							theChart.data.datasets.push({
								label: this.chartData.items.length === 1 ? 'Caddy Score' : ( item.label + ' Caddy Score' ),
								tooltips: item.tooltips,
								fill: false,
								hidden: this.hideCaddyScoreLine,
								backgroundColor: item.caddyScoreColor || '#f1492a',
								borderColor: item.caddyScoreColor || '#f1492a',
								data: item.caddyScoreData,
								yAxisID: 'y1',
								spanGaps: false,
								tension: 0.4
							});

							++ii;

						}

						if ( this.project.showCompetitors && item.competitors ) {

							Object.keys(item.competitors).forEach ( function (competitorDomain) {

								let c = item.competitors[competitorDomain];

								theChart.data.datasets.push({
									label: competitorDomain,
									tooltips: item.tooltips,
									fill: false,
									backgroundColor: c.color,
									borderColor: c.color,
									hidden: !this.project.competitors[competitorDomain],
									data: c.data,
									yAxisID: 'y',
									borderDash: c.borderDash || [5,5],
									spanGaps: false,
									tension: 0.4
								});

								this.competitorDataSet[ii] = competitorDomain;

								++ii;

							}.bind(this));

						}

					}.bind(this))

				}

			}

			return theChart;

		},

		competitors () {

			let c = [];

			if ( !this.project || !this.project.competitors ) return c;

			Object.keys( this.project.competitors ).forEach( competitorDomain => {

				if ( !competitorDomain || competitorDomain === 'undefined' ) return;

				c.push( competitorDomain );

			});

			return c;

		},

		tableHeadings () {

			let h = {

				c: { value: 'Select', checkBox: true, checkBoxVar: '_id' },
				keyword: { value: 'Keyword', component: LinkWithIcon, event: 'selectKeyword', 'clickVars': ['_id'], selectCheckbox: true, sort: -1 },
				currentRank: { value: 'Ranking', emptyCell: 'Pending...', component: RankUpDown, sort: 1, styles: "min-width:70px" },
				volume: { value: 'Search Vol.', emptyCell: 'Pending...', sort: -1, styles: "min-width:70px" },
				bestRank: { value: 'Best', emptyCell: 'N/A', component: RankUpDown, sort: 1, styles: "min-width:70px" },
				firstRank: { value: 'First', emptyCell: 'Pending...', component: RankUpDown, sort: 1, styles: "min-width:70px" },
				url: { value: 'URL', emptyCell: 'N/A', component: KeywordURL, sort: 1 }

			}

			if ( this.table.customDates && this.table.customDates.length > 0 ) {

				this.table.customDates.forEach( function (date) {

					h[date] = { value: date, component: RankUpDown, styles: date.length > 10 ? "min-width:110px" : "min-width:70px" };

				}.bind(this));

			}

			let h2 = {

				rankUpdated: { value: 'Updated', emptyCell: 'N/A', dateFormat: this.dateTimeFormat, sort: -1 },
				tagProcessed: { value: 'Tags' },
				lastConflict: { value: 'Last Cannibalisation', emptyCell: 'N/A', component: LinkWithIcon, sort: -1, hide: false, event: 'viewConflict' },
				frequencyName: { value: 'Frequency', click: 'updateFrequency', 'clickVars': ['_id','frequency','keyword'], hide: true },
				searchResults: { value: 'SERP', emptyCell: 'N/A', click: 'viewSearchResults', 'clickVars': ['_id','keyword'], styles: "min-width:70px" },
				features: { value: 'Features', component: Features, headingEvent: 'triggerShowFeaturesTip', tip: "Click for more info on Features" },
				pause: { value: '', valueForFilter: 'Pause', component: TableIcon, event: 'deletePause', hide: true },
				delete: { value: '', valueForFilter: 'Delete', component: TableIcon, event: 'deletePause', hide: true }

			}

			Object.keys(h2).forEach( function (key) {

				h[key] = h2[key];

			}.bind(this));

			if ( this.project.searchType === 'local' ) {

				delete h.features;
				delete h.lastConflict;

			}

			return h;

		},

		keywordCountText () {

			if ( typeof this.table.count !== 'undefined' ) return this.table.count + ' Keyword' + ( this.table.count !== 1 ? 's' : '' );

			return '';

		},

		theKeywordGraphTitle () {

			if ( !this.keywordGraphTitle ) {

				return this.keywordsSelectedProcessed.keywords.length === 1 ? this.keywordsSelectedProcessed.keywords[0] : ( this.keywordsSelectedProcessed.keywords.length + ' keywords' )

			}

			return this.keywordGraphTitle;

		},

		hasChartData () {

			return !( !!this.chartOverlay && !!this.chartOverlay.text && !( this.reloadingChart || !this.fetched ) );

		},

		mapSmallHeight () {

			if ( this.smallGraph ) return '180px';

			return !this.hasChartData ? '120px' : null;

		},

		chartOverlay () {

			if ( this.reloadingChart || !this.fetched ) return { text: 'Loading chart...' };

			if ( this.fetchedKeywords && !this.hasKeywords ) return { text: 'Please add some Keywords' };

			if ( this.tracked ) {

				if ( this.searchingGraph ) {

					const firstNonNull = this.chartData.items[0].data.find(item => item !== null);

					if ( !firstNonNull ) {

						let r = { text: 'There are no results for this search at the moment' };

						if ( !!this.filters.tags ) {

							r.text = 'There are no results for this tag(s) at the moment. If the tag is new, the data is being pieced together and will show here soon.';

						}

						return r;

					}

				}

				return false;

			}
			else {

				return { text: "Your Average Rank and Caddy Score graph will show here once we have more data points to display." };

			}

		},

		showAverageChart () {

			return this.tracked;

		},

		averageChartLabel () {

			return this.stillUpdating && this.chartOverlay === false ? 'Average Rank *** SOME STATS ARE STILL BEING BUILT AND WILL BE AVAILABLE SOON ***' : 'Average Rank';

		},

		specialOpsFilters () {

			return [
				{
					type: 'goneUp',
					label: 'Climbed'
				},
				{
					type: 'goneDown',
					label: 'Fallen'
				},
				{
					type: 'unChanged',
					label: 'Unchanged'
				},
				{
					type: 'changed',
					label: 'Changed'
				},
				{
					type: 'increase5',
					label: 'Top 30 Climbed 5+',
					group: 'top30'
				},
				{
					type: 'decrease5',
					label: 'Top 30 Fallen 5+',
					group: 'top30'
				},
				{
					type: 'lowHangingFruit',
					label: 'Low Hanging Fruit',
					noFrequency: true
				}
			];

		},

		projectOverview() {

			let overview = { standard: [], small: [] },
				frequency = this.frequencyDonuts === 'Daily' ? '' : this.frequencyDonuts;

			if ( this.project._id === 'undefined' && this.project.keywordCount > 0 ) return [];

			overview.standard = [
				{ total:this.project.keywordCount,label:'Active Keywords' },
				{ total:this.project.rankCaddyScore,label:'Caddy Score' }
			];

			['averageRank',3,10,50].forEach( function (n) {

				let type = isNaN(n) ? n : ( 'inTop'+n ),
					l = { 
						total: this.project[type], 
						label: isNaN(n) ? 'Average Rank' : ( 'Top ' + n ) 
					};

				if ( this.project[type+'Old'+frequency] && this.project[type+'Old'+frequency] !== 0 && this.project[type+'Old'+frequency] !== this.project[type] ) {

					let jump = this.project[type] - this.project[type+'Old'+frequency], label = '';

					if ( jump >= 0.1 || jump <= -0.1 ) {

						if ( n === 'averageRank' ) jump *= -1;
						
						if ( jump < 3 ) {

							label = jump.toFixed(2);
							
							label = label.split('.')[1] === '00' ? Math.round(parseFloat(jump)) : label;

						}
						else {

							label = Math.round(jump);
							
						}
						
						l.jump = Math.round(jump);

						l.jumpLabel = l.jump > 0 ? ( '+' + label ) : ( '-' + Math.abs( label ) );

						let neg = l.jump < 0;

						l.jump = Math.ceil( ( Math.abs( l.jump ) / this.project[type] ) * 100 );

						l.jump = l.jump < 6 ? 6 : l.jump;

						l.jump = l.jump > 100 ? 100 : l.jump;

						if ( neg ) {

							l.jump *= -1;

						}

					}

				}

				overview.standard.push( l );

			}.bind(this));

			this.specialOpsFilters.forEach ( function (small) {

				let f = small.type, p = this.project;

				if ( f === 'changed' ) {

					return

				}

				if ( !small.noFrequency ) f += frequency;

				if ( small.group ) p = p[small.group]; 

				overview.small.push( { total: p[f], label: small.label, click: 'clicked', clickVars: { type: small.type } } )

			}.bind(this));

			return overview;

		}, 
		keywordsSelectedProcessed () {

			let ids = new Set(), 
				keywords = [];

			this.keywordsSelected.forEach ( function (kId) {

				kId = kId.toString();

				const keyword = this.table.items.find( kw => kw._id.toString() === kId );

				if ( keyword ) {

					ids.add(kId);
					const k = typeof keyword.keyword === 'object' ? keyword.keyword.keyword : keyword.keyword;
					keywords.push( k );

				}

			}.bind(this));

			return { _ids: Array.from(ids), keywords: keywords };

		},
		keywords () {

			let keywords = [];

			if ( !this.fetched || typeof this.table.items === 'undefined' ) return keywords;

			this.table.items.forEach ( function (keyword) {

				let tags = {
					value: [],
					click: this.readOnly ? false : 'addTag',
					clickVars: keyword._id
				};

				if ( !this.readOnly ) {

					tags.label = 'Add tags';

				}

				if ( keyword.tags.length > 0 ) {

					keyword.tags.forEach ( function (tag,index) {

						if ( this.tagsProcessed.length === 0 ) return;
	
						let tagFound = this.tagsProcessed.find( t => t.value === tag );

						if ( !tagFound ) return;

						tags.value.push({ value: tag, colour: tagFound.colour });

					}.bind(this));

					if ( !this.readOnly ) {

						tags.label = 'Add/Edit tags';

					}

				}

				const customDates = this.table.customDates || [];

				['firstRank','bestRank','currentRank',...customDates].forEach( function (n) {

					if ( typeof keyword[n] !== 'undefined' ) {

						keyword[n] = { value: { value: keyword[n].value ? keyword[n].value.value : keyword[n], do101: true } }

						if ( n === 'currentRank' ) {
							
							keyword[n].value.changeRank = keyword['changeRank'+this.frequencyDonuts] * -1;

							if ( keyword.hasIndent ) {

								keyword[n].value.hasIndent = true;

							}

							if ( keyword.isFeatured ) {

								keyword[n].value.isFeatured = true;

							}

						}

					}

				}.bind(this));

				if ( !this.readOnly || tags.value.length ) {
				
					keyword.tagProcessed = tags;
					keyword.tagProcessed.value.sort((a, b) => a.value.localeCompare(b.value));

				}

				if ( keyword.processState === 'inactive' && !!keyword.noResults ) {

					['volume','bestRank','firstRank','url'].forEach( function (n) {

						if ( typeof keyword[n] !== 'undefined' ) {

							delete keyword[n];

						}

					}.bind(this));

					keyword.currentRank.value = { 
						changeRankNum: 0, 
						addBreak: false, 
						value: "No results.", 
						align: 'left' 
					};

				}

				keywords.push(keyword);

			}.bind(this));

			return keywords;

		},

		queryString () {

			let i = 0, 
				queryString = '';

			if ( this.tags.length < 2 ) {

				this.filters.average = false;

			}

			if ( this.reloadingChart ) {

				Object.keys(this.filters).forEach(function(k) {

					if ( this.filters[k] !== '' && this.filters[k] !== null ) {

						let a = i > 0 ? '&' : '?', 
							go = true, 
							val = ( k === 'startDate' || k === 'endDate' ) ? this.filters[k].getFullYear() + '-' + ( this.filters[k].getMonth() + 1 ) : this.filters[k];

						if ( k === 'startDate' || k === 'endDate' ) {

							let m = this.filters[k].getMonth() + 1, d = this.filters[k].getDate();

							if ( m < 10 ) m = '0' + m;
							if ( d < 10 ) d = '0' + d;

							val = this.filters[k].getFullYear() + '-' +  m + '-' + d;

						}
						else {

							if ( val === true ) val = 1;
							if ( val === false || val === null ) go = false;

						}

						if ( go ) {

							queryString += a + k + '=' + encodeURIComponent( val );
							++i;

						}

					}

				}.bind(this));

			}

			if ( i > 0 ) queryString += '&'; else queryString = '?';

			if ( !this.reloadingChart ) {

				if ( this.searches.customOrderBy ) {
					
					let c = this.searches.customOrderBy.split('--');

					queryString += 'sortBy=' + c[0] + '&sortDirection=' + c[1];

				}
				else {

					queryString += 'sortBy=' + this.sorting.sortBy + '&sortDirection=' + this.sorting.sortDirection;
				}

				queryString = this.sortSeaches(queryString);

				queryString += '&page=' + this.table.page;

			}

			if ( this.report ) {

				queryString += '&limit=50';

			}
			else {

				queryString += '&headings=' + this.showHeadings.join(',');

			}

			if ( this.pdfTitle ) queryString += '&pdfTitle=' + this.pdfTitle;

			return queryString;

		},
		showTableSearchFrequency () {
		return true;
			if ( this.searches.specialOps ) {

				let s = this.specialOpsFilters.find( s => s.type === this.searches.specialOps );

				if ( s ) return !s.noFrequency;

			}

			return false;

		},
		searchSetup() {

			let searchSetup = { 
				keyword: { type: 'text', placeholder: 'Enter a keyword to search', showLabel: 'Search Keywords', tip: "Filter by all or part of a search term." },
				url: { type: 'text', placeholder: 'Enter a url to search', showLabel: 'Search URLs', tip: "Filter by all or part of a URL." },
				specialOps: { 
					type: 'select', 
					multi: false, 
					showLabel: 'Opportunities', 
					tip: "Filter by useful insights and quick win opportunities.",
					items: [ 
						{ value: 'featured_snippets', label: 'Featured Snippets' },
						{ value: 'video_snippet', label: 'Video' }
					]
				},
				frequency: { 
					type: 'select', 
					multi: false, 
					showLabel: 'Compare', 
					hide: !this.showTableSearchFrequency,
					clearable: false,
					tip:"By default, the ranking difference arrows are based on rankings from the previous day. Change this filter to see the increases/decreases for different date ranges.",
					items: [ 
						{ value: 'Daily', label: 'Daily' },
						{ value: 'Weekly', label: 'Weekly' },
						{ value: 'Monthly', label: 'Monthly' },
						{ value: 'customDate', label: 'Custom Columns' }
					 ]
				},
				currentRankingMin: { type: 'number', placeholder: '', showLabel: 'Ranking From', hide: !!this.searches.specialOps, tip: "Filter by keywords within a particular ranking range." },
				currentRankingMax: { type: 'number', placeholder: '', showLabel: 'Ranking To', hide: !!this.searches.specialOps, tip: "Filter by keywords within a particular ranking range." },
				conflictsOnly: { 
					type: 'toggle', 
					label: '',
					label2: '', 
					click: 'conflictClick',
					showLabel: 'Cannibalisations Only?',
					tip: "When toggled on, a new column will appear with 'Last Cannibalisation' dates. Click on the date/binoculars to view the chart and investigate cannibalisation issues."
				},
				featureTypes: { 
					type: 'select', 
					multi: true, 
					showLabel: 'Features', 
					hide: !!this.searches.specialOps,
					tip:"Filter by the features available in Google results pages.",
					items: [ 
						{ value: 'ai_overview', label: 'AI Overview' },
						{ value: 'answer_box', label: 'Answer Box' },
						{ value: 'carousel', label: 'Carousel' },
						{ value: 'commercial_units', label: 'Commercial Units' },
						{ value: 'events', label: 'Events' },
						{ value: 'explore_brands', label: 'Explore Brands' },
						{ value: 'featured_snippet', label: 'Featured Snippet' },
						{ value: 'find_results_on', label: 'Find Results On' },
						{ value: 'google_flights', label: 'Flights' },
						{ value: 'google_hotels', label: 'Hotels' },
						{ value: 'google_posts', label: 'Posts' },
						{ value: 'google_reviews', label: 'Reviews' },
						{ value: 'hotels_pack', label: 'Hotels Pack' },
						{ value: 'images', label: 'Images' },
						{ value: 'jobs', label: 'Jobs' },
						{ value: 'knowledge_graph', label: 'Knowledge Graph' },
						{ value: 'local_pack', label: 'Local Pack' },
						{ value: 'local_services', label: 'Local Services' },
						{ value: 'map', label: 'Map' },
						{ value: 'mention_carousel', label: 'Mantion Carousel' },
						{ value: 'multi_carousel', label: 'Multi Carousel' },
						{ value: 'paid', label: 'Paid' },
						{ value: 'people_also_ask', label: 'People Also Ask' },
						{ value: 'people_also_search', label: 'People Also Search' },
						{ value: 'podcasts', label: 'Podcasts' },
						{ value: 'popular_products', label: 'Popular Products' },
						{ value: 'questions_and_answers', label: 'Questions And Answers' },
						{ value: 'recipes', label: 'Recipes' },
						{ value: 'related_searches', label: 'Related Searches' },
						{ value: 'scholarly_articles', label: 'Scholarly Articles' },
						{ value: 'shopping', label: 'Shopping' },
						{ value: 'stocks_box', label: 'Stocks Box' },
						{ value: 'top_sights', label: 'Top Sights' },
						{ value: 'top_stories', label: 'Top Stories' },
						{ value: 'twitter', label: 'Twitter' },
						{ value: 'video', label: 'Video' },
						{ value: 'visual_stories', label: 'Visual Stories' } 
					 ]
				},
				customOrderBy: { 
					type: 'select', 
					multi: false, 
					showLabel: 'Alternate Ordering', 
					tip:"Sort results by a field that isn't a table heading.",
					items: [ 
						{ value: 'changeRank--asc', label: 'Rank Change ↑' },
						{ value: 'changeRank--desc', label: 'Rank Change ↓' }
						
					]
				}

			};

			if ( this.project.searchType === 'local' ) {

				delete searchSetup.conflictsOnly;
				delete searchSetup.featureTypes;
				searchSetup.specialOps.items = [];

			}

			this.specialOpsFilters.forEach( function (f) {

				searchSetup.specialOps.items.push( { value: f.type, label: f.label } )

			}.bind(this));

			if ( this.searches.featureTypes && Array.isArray( this.searches.featureTypes ) && this.searches.featureTypes.length > 0 ) {

				searchSetup.featureFilterBy = { 
					type: 'select', 
					showLabel: 'Where Domain...', 
					hide: !!this.searches.specialOps,
					tip: "Are the selected features for this domain?",
					items: [ 
						{ value: 'has-feature', label: 'Has Selected Feature(s)' },
						{ value: 'no-feature', label: 'Does Not Have Selected Feature(s)' }
					 ]
				}

			}

			if ( this.tagsProcessed.length > 0 ) {

				searchSetup.tags = {
					showLabel: 'Tags', 
					type: 'select',
					multi: true,
					tip: "Filter by your custom tags.",
					items: this.tagsProcessed
				}

			}

			return searchSetup;

		},
		sorting () {

			let sorted;
				
			if ( this.tableName && this.tableSortingInfo[this.tableName] ) {

				sorted = this.tableSortingInfo[this.tableName];
				this.table.sorted = sorted;

			}
			else {

				sorted = this.table.sorted;

			}

			let sortBy = Object.keys(sorted)[0],
				sortDirection = sorted[sortBy] === 1 ? 'asc' : 'desc';

			return { sortBy: sortBy, sortDirection: sortDirection }

		},

		projectId () {

			return this.$route.params.projectId.toString();

		},

		tableName () {

			return 'project-keywords';

		},
		tagsProcessed () {

			let tags = [];

			this.tags.forEach( function (tag,index) {

				tags.push({ value: tag, label: tag, colour: this.hex(index) });

			}.bind(this));

			tags.sort((a, b) => a.value.localeCompare(b.value));

			if ( tags.length > 0 ) {

				tags.unshift({ value: 'empty-tags', label: 'No Tags', colour: '#000000' });

			}

			return tags;

		},

		breakBeforeGraph () {

			return ( this.showForReport && ( this.showForReport.graph || this.showForReport.filters || this.showForReport.buttons || this.showForReport.overview ) );

		},

		frequencly () {

			const f = ['Daily','Weekly','Monthly'];

			let index = f.indexOf(this.searches.frequencySearch);

			if ( index < 0 ) index = 0;

			return f[index];

		},
		insightsTitle () {

			return 'Rank Tracking Insights - ' + this.project.name;

		}

	},
	methods: {

		...mapMutations('general',['updateLoadingText','updateVar','tableHeading','reloadInstructionalTips']),

		closeInsights () {

			this.showInsights = false;

		},

		openInsights () {

			this.showInsights = true;

		},

		addedCustomDate(heading) {

			if ( heading ) {

				let showHeadings = general.stripObject( this.showHeadings );
				showHeadings.push(heading);
				this.showHeadings = showHeadings;

			}
			
			this.fetchKeywords ();

		},

		triggerShowFeaturesTip() {

			this.showFeaturesTip = true;
		},


		handleChartClick (ob) {



		},

		minimizeGraph() {

			this.smallGraph = !this.smallGraph;
			++this.chartKey;

		},

		videoImage (videoId) {

			return require('./../assets/img/video-thumbs/'+videoId+'.jpg');

		},

		projectNameUpdated (name) {

			this.project.name = name;

		},

		closeVideo () {

			this.showVideo = false;
			general.modalClose();

		},

		loadVideo ( videoId ) {
			
			this.videoId = videoId;
			this.showVideo = true;

		},

		addKeywords (ev) {

			if ( ev && !!ev.preventDefault ) ev.preventDefault();

			if ( this.project.isAdmin ) {

				this.showKeywordModal=true;
				return;

			}

			general.$snotify(this,'error','Add Keywords',"The project owner needs to grant you Full Access to this project to allow adding keywords");

		},

		saveCompetitors (info) {

			if ( typeof info !== 'undefined' ) {

				if ( typeof info === 'object' ) {

					if ( typeof info.competitors !== 'undefined' ) Vue.set( this.project, 'competitors', info.competitors );

					if ( typeof info.showCompetitors !== 'undefined' ) Vue.set( this.project, 'showCompetitors', info.showCompetitors );

				}
				else if ( typeof info === 'boolean' && info ) {

					info = { competitors: this.project.competitors, showCompetitors: this.project.showCompetitors };

				}

				if ( info ) {

					ApiService.put( 'projects/'+this.$route.params.projectId+'/competitors', info )
					.then( function( response ) {

						if ( response.data.updateGraph ) {

							this.updateGraph ();

						}

					}.bind(this))
					.catch( function(err) {}.bind(this));

				}

			}

		},

		closeReportOptions( fileType, page, pdfTitle ) {

			if ( fileType ) {

				this.pdfTitle = pdfTitle;

				this.fetchKeywords ( 1655, false, fileType, page );

			}

			this.showReport = false;

			general.modalClose();

		},

		downloadKeywords (fileType) {

			this.$dialog
				.confirm( "Are you sure you want to create a " + fileType.toUpperCase() + "?", { okText: 'Yes', cancelText: 'Cancel'})
				.then(function() {

					if ( this.table.pages > 1 || fileType === 'pdf' ) {

						this.showReport = fileType;

					}
					else {

						this.fetchKeywords ( 1678, false, fileType );

					}

				}.bind(this))
				.catch(function() {});

		},

		showingProjectOverviewSmalls (t) {
			
			this.showSmalls = t;

		},

		showIt (type) {

			return ( this.fetchedProject && ( !this.report || this.showForReport[type] ) );

		},

		chartRendered () {
			
			this.$emit('chart:ready');

		},

		hex (index) {

			let ops = ['#3B445B', '#e6194B', '#3cb44b','#4363d8', '#911eb4', '#f032e6', '#469990', '#9A6324', '#000000', '#800000', '#808000', '#000075', '#a9a9a9', '#000000', '#555555', '#10daf2'], 
				l = ops.length - 1;

			if ( index > l ) {

				index = index%l;

			}

			return ops[index];

		},

		numberTotalSectionClick(info) {
	
			if ( info.clickVars && info.clickVars.type ) {

				this.setTableSearchValue( 'specialOps', info.clickVars.type );
				general.scrollIntoView("keywords-table");
				this.setTableSearchValue( 'search', true );

			}

		},

		tableReady () {
			
			this.setDonutFrequency( this.frequencyDonuts );

		},

		setTableSearchValue( k, v ) {

			Vue.set( this.forceSearchFieldUpdate, k, v );

		},

		setDonutFrequency(frequency) {

			this.frequencyDonuts = frequency;
			this.setTableSearchValue( 'frequency', frequency );

			this.projectOverviewKey = new Date().getTime();

			if ( this.ranSearchWithFreqency ) {

				this.setTableSearchValue( 'search', true );

			}

		},

		closeRankingsCustomDate () {

			this.showRankingsCustomDate = false;

		},

		searchChange (searches) {

			if ( this.init ) {

				this.init = false;
				return;

			}
			
			if ( searches.frequency ) {

				if ( searches.frequency === 'customDate' ) {

					this.setTableSearchValue( 'frequency', this.frequencyDonuts );
					this.showRankingsCustomDate = true;

				}
				else {

					this.frequencyDonuts = searches.frequency;

				}

			}
			
			let sc = searches.conflictsOnly || false;

			if ( sc !== this.conflictsOnlyLast ) {

				this.conflictsOnlyLast = sc;

				let showHeadings = general.stripObject( this.showHeadings ), 
					headingHasLastConflict = showHeadings.indexOf('lastConflict');

				if ( sc ) {

					if ( headingHasLastConflict < 0 ) showHeadings.push('lastConflict');

				}
				else if ( this.conflictsOnlyLast ) {

					if ( headingHasLastConflict >= 0 ) showHeadings.splice(headingHasLastConflict,1);

				}

				this.showHeadings = showHeadings;

				Vue.set( this.table, 'page', 1 );
				this.fetchKeywords (1795);

			}

			this.searches = searches;

		},

		conflictsFixed () {

			this.$dialog
				.confirm( "Mark These Keyword Cannibalisations As Fixed?", { okText: 'Yes', cancelText: 'Cancel',})
				.then(function() {

					this.updateLoadingText("One moment...");

					ApiService.put( 'stats/conflicts/'+this.$route.params.projectId+'/fixed', this.keywordsSelectedProcessed._ids )
					.then( function( info ) {

						this.updateLoadingText();
						this.fetchKeywords (1815);

					}.bind(this))
					.catch( function(err) {

						this.updateLoadingText();

					}.bind(this));

				}.bind(this))
				.catch(function() {});

		},

		addAlert () {

			this.showAlertsOverlay = true;

		},

		closeAlertsOverlay ( info ) {

			this.showAlertsOverlay = false;

			if ( info ) {

				this.$snotify.success("Your alert has been created", 'Alert');

			}

			general.modalClose();

		},

		addTags () {

			this.addTag( this.keywordsSelected );

		},

		refreshAll () {

			let top = (window.pageYOffset || document.documentElement.scrollTop)  - (document.documentElement.clientTop || 0);

			this.getProject ( function () {

				setTimeout( function () { window.scrollTo( 0, top ); }, 1);

			})
			this.fetchKeywords (1864);
			this.updateGraph ();

		},

		csvImportsUpload () {

			this.instantUpload = true;
			this.showKeywordModal = true;
			this.showCSVImportsOverlay = false;

		},

		showCSVImports () {

			this.showCSVImportsOverlay = true;

		},

		closeCSVImports () {

			this.showCSVImportsOverlay = false;

			general.modalClose();

		},

		uploadCSV () {

			this.showUploader = true;

		},

		closeUploader (info) {

			this.showUploader = false;

			general.modalClose();

		},

		viewSearchResults ( { _id, keyword } ) {

			this.updateLoadingText('Fetching results...');

			ApiService.get( 'keywords/search-results/' + this.$route.params.projectId + '/' + _id )
			.then( function( info ) {

				this.updateLoadingText();
				this.serpResults.results = info.data;
				this.serpResults.keyword = typeof keyword === 'object' ? keyword.keyword : keyword;

			}.bind(this))
			.catch( function(err) {

				this.updateLoadingText();

			}.bind(this));

		},

		conflictClick () {

			this.showConflictsOnly = !this.showConflictsOnly;

			if ( this.showConflictsOnly ) {
				this.table.sorted = { lastConflict: -1 };
				
			}

			this.table.page = 1;
			this.fetchKeywords (1935);

		},

		updateFrequencyMulti () {

			this.updateFrequency ( { _id: this.keywordsSelectedProcessed._ids, frequency: 'multi' } );

		},

		updateFrequency (info) {

			let title = "Update frequency for ";

			if ( Array.isArray( info._id ) ) {

				title += info._id.length > 1 ? ( info._id.length + " keywords" ) : '1 keyword';

			}
			else {

				title += typeof info.keyword === 'object' ? info.keyword.keyword : info.keyword;

			}

			this.keywordModalInfo = { title: title, currentFrequency: info.frequency, _id: info._id };

			this.showKeywordModal = true;

		},

		sort(info) {

			Vue.set( this.table, 'sorted', info );
			Vue.set( this.table, 'page', 1 );

			if ( info.init ) { delete this.table.sorted.init; return; }

			this.fetchKeywords (1973);

		},

		clearSearch () {

			this.searcheCleared = true;
			Vue.set( this.table, 'search', {} );
			let f = this.searches.frequency;
			Vue.set( this, 'searches', {} );
			this.searches.frequency = f;
			Vue.set( this.table, 'page', 1 );
			this.fetchKeywords (1985);

		},

		search(info) {

			if ( this.searcheCleared ) {

				this.searcheCleared = false;
				return;

			}

			Vue.set( this.table, 'search', info );
			Vue.set( this.table, 'page', 1 );
			this.fetchKeywords (2000);

		},

		pagination(info) {

			Vue.set( this.table, 'page', info.page );
			this.fetchKeywords (2007);

		},

		addKeywordsToAnotherProject () {

			if ( this.project.isAdmin ) {
				
				this.keywordAdderKeywords = this.keywordsSelectedProcessed.keywords;
				this.showKeywordModal = true;
				return;

			}

		},

		pauseDeleteMulti (type) {

			this.deletePause ({ _id: this.keywordsSelectedProcessed._ids, action: type },true);

		},

		deletePause (info,multi) {

			let a = 'delete', 
				a2 = 'Deleting', 
				action = 'delete', 
				uri = "keywords/" + this.$route.params.projectId + '/' + ( Array.isArray( info._id ) ? info._id.join(',') : info._id ), 
				xtra = " (this can't be reversed!)",
				update = {},
				n;

			if ( info.action === 'pause' ) {

				a = 'pause';
				a2 = 'Pausing';
				action = 'put';
				uri = "keywords/change-state/" + this.$route.params.projectId;
				xtra = "";
				update = { state: 'inactive', _ids: [info._id] };

			}

			if ( info.action === 'un-pause' ) {

				a = 'un-pause';
				a2 = 'Un-Pausing';
				action = 'put';
				uri = "keywords/change-state/" + this.$route.params.projectId;
				xtra = "";
				update = { state: 'active', _ids: [info._id] };

			}

			if ( typeof multi !== 'undefined' ) {

				update._ids = info._id;
				n = update._ids.length + ' keywords'

			}
			else {

				n = info.name + xtra;

			}

			this.$dialog
				.confirm( "Are you sure you want to " + a + " " + n + "?", { okText: 'Yes please', cancelText: 'Cancel',})
				.then(function() {

					this.updateLoadingText(a2+", one moment..");

					ApiService[action]( uri, update )
					.then( function( info ) {

						this.updateLoadingText();
						this.refreshAll();

					}.bind(this))
					.catch( function(err) {

						this.updateLoadingText();
						this.$snotify.error(err.response.data.error, a2+' keyword');

					}.bind(this));

				}.bind(this))
				.catch(function() {
					

				});

		},

		closeSERPresults () {

			this.serpResults.results = [];

			general.modalClose();

		},

		closeKeywordModal (info) {

			let refreshAll = true;

			if ( this.keywordAdderKeywords.length > 0 ) {

				this.keywordAdderKeywords = [];
				refreshAll = false;

			}

			this.showKeywordModal = false;

			this.instantUpload = false;

			this.keywordModalInfo = { title: null, currentFrequency: null };

			general.modalClose();

			if ( typeof info !== 'undefined' ) {

				if ( typeof info.keywords !== 'undefined' ) {
					
					let s = info.keywords.split(/\r\n|\r|\n/).length === 1 ? '' : 's';

					this.updateLoadingText('Adding keyword'+s+'...');

					ApiService.post( 'keywords/' + info.projects.join(','), info )
					.then( function( info ) {

						if ( refreshAll ) this.refreshAll();
						this.updateLoadingText();

						let results = { ok: 0, error: 0, existed: [] };

						info.data.forEach( function (n) {

							if ( n.error ) {
								++results.error;
								if ( n.errorType === "exists" ) {
									results.existed.push( n.keyword );
								}
							}
							else ++results.ok;

						});

						if ( results.ok > 0 ) {

							s = results.ok.length === 1 ? '' : 's';

							this.$snotify.success(results.ok + " keyword" + s + ( s === '' ? ' has' : ' have' ) + " been added", 'Add Keywords');

						}

						if ( results.error > 0 ) {

							s = results.error.length === 1 ? '' : 's';

							let errorMsg = results.error + " keyword" + s + " had errors. ";

							if ( results.existed.length > 0 ) {

								errorMsg += results.existed.join(', ') + " already existed in the project(s)";

							}

							this.$snotify.error( errorMsg, 'Add Keywords');

						}

					}.bind(this))
					.catch( function(err) {
						console.error(err);
						this.updateLoadingText();
						this.$snotify.error("Your keyword" + s + " could not be added, do you have permission to do this?", 'Add Keyword'+s);

					}.bind(this));

				}
				else {

					let title = "Updating frequency for ";

					if ( Array.isArray( info._id ) ) {

						title += info._id.length > 1 ? ( info._id.length + " keywords" ) : '1 keyword';

					}
					else {

						title += typeof info.keyword === 'object' ? info.keyword.keyword : info.keyword;

					}

					this.updateLoadingText( title );

					let id = Array.isArray(info._id) ? info._id.join(',') : info._id;

					ApiService.put( 'keywords/update-frequency/' + this.projectId + '/' + id, { frequency: info.frequency } )
					.then( function( info ) {

						this.fetchKeywords (2211);
						this.updateLoadingText();

					}.bind(this))
					.catch( function(err) {

						this.updateLoadingText();

					}.bind(this));

				}

			}

		},

		closeKeywordGraph () {

			general.modalClose();
			this.showKeywordGraph = false;
			if ( this.keywordsSelected.length === 1 ) this.keywordsSelected = [];
			this.isConflict = false;
			this.keywordGraphEndDate = null;
			this.keywordGraphTitle = null;

		},

		zoomChange (zoom) {

			Vue.set( this, 'zoom', zoom );

		},

		filterChange (filters) {

			filters.tags = this.tagsSelected.join('|');

			this.filters = filters;

			this.updateGraph ();

		},

		updateGraph () {

			this.reloadingChart = true;

			let url = 'stats/keywords/'+this.$route.params.projectId + this.queryString;

			ApiService.get( url )
			.then( function( info ) {

				Vue.set( this, 'chartData', info.data );
				
				this.fetched = true;
				this.reloadingChart = false;
				this.frequency = info.data.frequency;
				this.tracked = info.data.tracked;
				this.stillUpdating = info.data.stillUpdating;

			}.bind(this))
			.catch( function(err) {

				this.updateLoadingText();

			}.bind(this));

		},

		getProject ( callback, updateFetched = true ) {

			if ( updateFetched ) this.fetchedProject = false;

			ApiService.get( 'projects/' + this.$route.params.projectId )
			.then( function( info ) {

				if ( info.data ) {

					if ( updateFetched ) this.fetchedProject = true;
					Vue.set( this, 'project', info.data );
					if ( callback ) callback(true);

					if ( this.awaitingNewResults ) {

						setTimeout( function () {
							this.getProject( null, false );
						}.bind(this), 5000 );

					}

				}
				else {

					this.notFound = true;
					this.updateLoadingText();

				}

			}.bind(this))
			.catch( function(err) {

				this.updateLoadingText();
				this.notFound = true;

			}.bind(this));

		},

		closeTagger (info) {

			this.showTagger = false;

			general.modalClose();

			if ( typeof info !== 'object' || Object.keys( info ).length === 0 ) return;

			this.updateLoadingText("Updating tags");

			ApiService.put( 'keywords/update-tags/' + this.$route.params.projectId, { tags: info.tags, _ids: info.keywordIds, merge: !this.taggerSettings.single } )
			.then( function( info ) {

				this.updateLoadingText("Reloading keywords");

				this.fetchKeywords (2326);

			}.bind(this))
			.catch( function(err) {

				this.updateLoadingText();

			}.bind(this));

		},

		addTag ( keywordIds ) {

			if ( !Array.isArray( keywordIds ) ) keywordIds = [keywordIds];

			let tags = [], single = keywordIds.length === 1;

			if ( keywordIds.length === 1 ) {

				let kw = this.table.items.find( kw => kw._id.toString() === keywordIds[0].toString() );

				if ( kw ) {

					tags = kw.tags;

				}

			}

			this.taggerSettings = { 
				tags: tags, 
				returnOnly: true, 
				tagName: 'Tag', 
				title: 'Add Tags', 
				availableTags: this.tags, 
				keywordIds: keywordIds,
				returns: ['keywordIds'],
				single: single,
				saveOnClose: true
			};

			this.showTagger = true;

		},

		checkboxFilterChange (info) {

	

		},

		keywordActions(ids) {

			Vue.set(this,'keywordsSelected',ids);

		},

		selectKeyword ( { _id } ) {

			this.keywordsSelected = [_id];
			this.showKeywordGraph = true;

		},

		viewConflict ( { _id, lastConflict, keyword } ) {

			this.keywordsSelected = [_id];
			this.isConflict = true;
			this.keywordGraphEndDate = new Date( lastConflict );
			this.showKeywordGraph = true;
			this.keywordGraphTitle = keyword;

		},

		sortSeaches ( queryString ) {

			let searches = {}, isQueryString = typeof queryString === 'string';

			queryString = isQueryString ? queryString : '';

			Object.keys(this.searches).forEach( function (k) {

				if ( k === 'customOrderBy' ) return;

				if ( this.searches[k] !== '' && this.searches[k] !== null ) {

					let v = this.searches[k];

					if ( k !== 'frequency' ) {

						if ( typeof v === 'boolean' ) {

							if ( v === false ) return;

							v = 1;

						}

						queryString += '&' + k + '=' + encodeURIComponent( v );

						searches[k] = v;

					}
					else {

						queryString += '&frequencySearch=' + v;
						searches['frequencySearch'] = v;
					}

				}

			}.bind(this));

			return isQueryString ? queryString : searches;

		},

		fetchKeywords (label,noLoad,download,downloadPage,callback) { // fetch keywords

			if ( !this.$route.params.projectId ) return;

			let url = 'keywords/'+this.$route.params.projectId;

			if ( download ) {

				url += '/download/' + download;
				this.updateLoadingText("One moment...");

			}

			url += this.queryString;

			if ( downloadPage === 0 ) url += '&allPages=1';

			if ( !noLoad && !download ) this.table.loading = true;
			
			if ( noLoad && this.keywordsSelected.length > 0 ) {

				if ( !this.fetchKeywordsTimeoutSet ) {
					
					this.fetchKeywordsTimeoutSet = true;

					setTimeout( function () {
						this.fetchKeywordsTimeoutSet = false;
						this.fetchKeywords (2428,true);
					}.bind(this), 10000 );

				}

				return;

			}

			let data = download ? { responseType: 'arraybuffer' } : {};
			
			ApiService.get( url, data )
			.then( function( info ) {
				
				this.updateLoadingText();

				if ( download ) {

					const type = info.headers['content-type']
					const blob = new Blob([info.data], { type: type, encoding: 'UTF-8' })
					const link = document.createElement('a')
					link.href = window.URL.createObjectURL(blob)
					link.download = this.project.nameLowercase.replaceAll(' ','-') + '.' + download;
					link.click();
					return;

				}

				let s = { ...this.table.search };

				this.table = info.data;
				Vue.set(this, 'table', info.data);
				this.readOnly = this.table.readOnly;
				this.table.search = s;

				this.table.loading = false;

				if ( !this.fetchedKeywords ) {

					this.fetchedKeywords = true;
					this.hasKeywords = this.table.items.length > 0;

				}

				if ( Object.keys(s).length === 0 && this.table.count > 0 ) {

					this.hasKeywords = true;

				}

				let reload = false;

				this.table.items.forEach( function (kw) {

					if ( kw.processState === 'active' && ( !kw.currentRank || ( !kw.volumesNotAllowedLanguage && ( !!kw.volumePendingUpdate || !kw.volumeUpdated ) ) ) ) {

						reload = 10;

					}

				}.bind(this));
				
				if ( reload && !this.fetchKeywordsTimeoutSet ) {
					
					this.fetchKeywordsTimeoutSet = true;

					setTimeout( function () {
						this.fetchKeywordsTimeoutSet = false;
						this.fetchKeywords (2497,true);
					}.bind(this), reload * 1000 );

				}

				this.tags = info.data.tags;

				if ( Array.isArray( this.tags ) && this.tags.length > 0 ) {

					if ( typeof this.tags[0].value === 'object' ) {

						this.tags.sort((a, b) => a.value.localeCompare(b.value));

					}
					else {

						this.tags.sort((a, b) => a.localeCompare(b));

					}

				}

				this.ranSearchWithFreqency = !!this.showTableSearchFrequency;

				if ( typeof callback === 'function' ) callback();

			}.bind(this))
			.catch( function(err) {
				console.error('fetchKeywords',err);
			}.bind(this));

		},
		setSearchFromHash () {

			let h = window.location.hash.replace('#','');

			switch ( h ) {

				case 'featured_snippets' :
				case 'video_snippet' :
				case 'lowHangingFruit' :

					Vue.set( this.table, 'search', { specialOps: h } );
					Vue.set( this, 'searches', { specialOps: h } );
					this.setTableSearchValue( 'specialOps', h );
					this.setTableSearchValue( 'search', true );

				return true;

				case 'conflictsOnly' :

					this.conflictsOnlyLast = true;
					let showHeadings = general.stripObject( this.showHeadings ), 
						headingHasLastConflict = showHeadings.indexOf('lastConflict');
					if ( headingHasLastConflict < 0 ) showHeadings.push('lastConflict');
					this.showHeadings = showHeadings;
					Vue.set( this.table, 'search', { conflictsOnly: true } );
					Vue.set( this, 'searches', { conflictsOnly: true } );
					this.setTableSearchValue( 'conflictsOnly', true );
					this.setTableSearchValue( 'search', true );

				return true;

			}

			return false;

		},
		showConflicts ( searches ) {



		},
		tagEditor () {
			
			this.closeTagger();
			this.showTagEditor = true;

		},
		closeTagEditor (reload) {

			this.showTagEditor = false;

			if ( reload ) {

				this.fetchKeywords (2582);

				this.updateGraph ();

			}

		}

	},
	created() {

		this.updateLoadingText(true);

		this.getProject (function (found) {

			this.updateLoadingText(false);

			if ( found ) {
				this.fetchKeywords ( 2600, null, null, null, function () {
					let doIt = this.setSearchFromHash ();
					if ( doIt ) {
						this.updateLoadingText(true); 
						this.fetchKeywords (2604, true, null, null, function () {
							general.scrollIntoView("keywords-table");
						}.bind(this));
					}
					this.reloadInstructionalTips();
				}.bind(this));
				this.updateGraph ();
				TokenService.saveProject(this.$route.params.projectId);
				this.$emit('project-info',this.project);

			}

		}.bind(this));

	}
}
</script>

<style lang="less" scoped>
@import './../assets/css/less/_helper.less';
@import './../assets/css/less/_settings.less';
@imgRoot:'../../';
.tag-editor {
	margin-top: 20px;
	span {
		color: @formDefaultColour;
		cursor: pointer;
		&:hover {
			text-decoration: underline;
		}
	}
}
.filter-wrap {
	.v-select {
		border: 1px solid rgba(0,0,0,.1);
		background: #fff;
	}
}
.video-item {

	cursor: pointer;

	.video-title {

		margin-top: 5px;

	}

	.video-thumb {

		position: relative;
		cursor: pointer;
		margin-top: 20px;

		&:last-child {

			margin-top: 0;

		}

		&:after {

			display: block;
			position: absolute;
			background:url( './../assets/img/icons/video.svg' ) center center no-repeat;
			width: 50px;
			height: 50px;
			content: "";
			margin: -25px 0 0 -25px;
			left: 50%;
			top: 50%;
			.opacity(0.7);

		}

		img {

			max-width: 100%;

		}

	}

	&:hover {

		.video-thumb {

			img {

				.opacity(0.8);

			}

			&:after {

				.opacity(1);

			}

		}

	}

}
.action-button {
	background-color: @formDefaultColour;
	color: #fff;
	display: inline-block;
	border-radius: 4px;
	font-size: 14px;
	cursor: pointer;
	margin: 0;
	padding: 2px 6px;
	&:hover {
		color: @formDefaultColour;
		background-color: #fff;
	}
	&.right {
		float: right;
	}
}
.action-buttons-float {
	.clearfix;
	text-align: center;
	.large-button {
		margin:0;
	}
}

.svg {

	background-color: @darkContrast;
	vertical-align: top;
	width: 20px;
	height: 20px;
	display: inline-block;
	padding: 4px;
	position: relative;
	.rounded(50%);
	.trans;
	cursor: pointer;
	
	svg {

		fill:#fff;
		color: #fff;
		width:50%;
		display: block;
		position: absolute;
		left: 25%;
		top: 10%;
		.trans;

	}

	&.minimize {

		position: absolute;
		top: 11px;
		right: 11px;
		z-index: 2;

	}

	&:hover {

		background-color: #fff;

		svg {

			fill: @darkContrast;
			color: @darkContrast;

		}

	}

	&.white {

		background-color: #fff;

		svg {

			fill: @darkContrast;
			color: @darkContrast;

		}

	}

}

@media screen and (min-width: 540px) {

	.event-actions {

		position: absolute;
        right: 40px;
        top: 10px;

	}

	.video-item {

		display: inline-block;
		margin-top: 20px;
		width: 100%;
		text-align: left;

		.video-thumb {

			display: inline-block;
			width: 36%;
			margin-right: 2%;
			box-sizing: border-box;
			margin-top: 0;
			vertical-align: middle;
			max-width: 200px;

		}

		.video-title {

			display: inline-block;
			width: 60%;
			text-align: left;
			box-sizing: border-box;
			margin-top: 0;
			vertical-align: middle;

		}

	}

}

@media screen and (min-width: 768px) {
	.video-item {

		width: 48%;
		margin-left: 2%;

		.video-thumb {

			margin-right: 4%;

		}

		.video-title {

			width: 50%;

		}

	}
	.keyword-table {
		&.showing-headings {
			position: relative;
			top: -10px;
		}
	}
}
@media screen and (min-width: 768px) {
	.video-item {

		width: 30%;
		max-width: 300px;

	}
}
</style>
